import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import {
  getAiHubStatusChipSx,
  type AiHubStatusTone,
} from "../aiHubChipStyles";
import TriageResultRowActions from "./TriageResultRowActions";
import TriageResultTableToolbar from "./TriageResultTableToolbar";
import { buildAiHubTriageResultPath } from "../constants";
import {
  TRIAGE_FLOW_STATUS_LABELS,
  type TriageFlowStatus,
  type TriageResultRow,
} from "./types";

type TriageResultTableProps = Readonly<{
  rows: TriageResultRow[];
  rowCount: number;
  exportRows: TriageResultRow[];
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}>;

function TriageResultNoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.
      </Typography>
    </Box>
  );
}

function flowStatusTone(status: TriageFlowStatus): AiHubStatusTone | null {
  switch (status) {
    case "notStarted":
      return "yellow";
    case "completed":
      return "green";
    case "inProgress":
      return "blue";
    case "terminate":
      return "red";
    default:
      return null;
  }
}

function formatCreateDate(createDateIso: string): string {
  const parsed = dayjs(createDateIso);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : "-";
}

function TriageResultIdLink({ resultId }: { resultId: string }) {
  const navigate = useNavigate();

  return (
    <Link
      component="button"
      type="button"
      underline="always"
      aria-label={`Open triage result ${resultId}`}
      sx={{
        color: "#295EF1",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        textAlign: "left",
      }}
      onClick={(event) => {
        event.stopPropagation();
        navigate(buildAiHubTriageResultPath(resultId));
      }}
    >
      {resultId}
    </Link>
  );
}

export default function TriageResultTable({
  rows,
  rowCount,
  exportRows,
  paginationModel,
  onPaginationModelChange,
}: TriageResultTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const toolbarSlot = useCallback(
    () => <TriageResultTableToolbar exportRows={exportRows} />,
    [exportRows],
  );

  const columns = useMemo<GridColDef<TriageResultRow>[]>(
    () => [
      {
        field: "resultId",
        headerName: "Result ID",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <TriageResultIdLink resultId={row.resultId} />
          </Box>
        ),
      },
      {
        field: "objectTypeName",
        headerName: "Object Type",
        width: 120,
        sortable: false,
        filterable: false,
      },
      {
        field: "objectSubTypeName",
        headerName: "Object Sub-Type",
        width: 145,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.objectSubTypeName?.trim() || "-",
        renderCell: ({ value }) => {
          const text = typeof value === "string" ? value.trim() : "";
          const isEmpty = !text || text === "-";
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: isEmpty ? "#8c8c8c" : "inherit",
                  fontSize: "14px",
                }}
              >
                {isEmpty ? "-" : text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "objectId",
        headerName: "Object ID",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Link
              component="button"
              type="button"
              underline="always"
              disabled
              aria-label={`Open ${row.objectId} (coming soon)`}
              sx={{
                color: "#295EF1",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "default",
                textAlign: "left",
                "&.Mui-disabled": {
                  color: "#295EF1",
                  opacity: 1,
                },
              }}
            >
              {row.objectId}
            </Link>
          </Box>
        ),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.2,
        minWidth: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: "version",
        headerName: "Version",
        width: 90,
        sortable: false,
        filterable: false,
      },
      {
        field: "triageResult",
        headerName: "Triage Results",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const text = row.triageResult.trim();
          const isEmpty = !text;
          if (isEmpty) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  -
                </Typography>
              </Box>
            );
          }
          if (row.triageResultIsLink) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  disabled
                  aria-label={`Open ${text} (coming soon)`}
                  sx={{
                    color: "#295EF1",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "default",
                    textAlign: "left",
                    "&.Mui-disabled": {
                      color: "#295EF1",
                      opacity: 1,
                    },
                  }}
                >
                  {text}
                </Link>
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography noWrap title={text} sx={{ fontSize: "14px" }}>
                {text}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "flowStatus",
        headerName: "Flow Status",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const tone = flowStatusTone(row.flowStatus);
          const label = TRIAGE_FLOW_STATUS_LABELS[row.flowStatus];
          if (!tone || label === "-") {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  -
                </Typography>
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Chip
                size="small"
                label={label}
                sx={getAiHubStatusChipSx(tone)}
              />
            </Box>
          );
        },
      },
      {
        field: "createDate",
        headerName: "Create Date",
        width: 130,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => formatCreateDate(row.createDate),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-hub-actions-cell",
        renderCell: ({ row }) => (
          <TriageResultRowActions resultId={row.resultId} />
        ),
      },
    ],
    [],
  );

  const isEmpty = rows.length === 0;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.resultId}
        rowHeight={48}
        checkboxSelection
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        disableDensitySelector={false}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        disableColumnPinning={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: TriageResultNoRowsOverlay,
          columnsManagement: columnsManagementSlot,
        }}
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        sx={
          [
            getRegulationsListDataGridSx(primaryColor),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              height: "100%",
              minHeight: 400,
              "& .MuiDataGrid-toolbarQuickFilter": {
                display: "none",
              },
              "& .ai-hub-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
                "& .MuiDataGrid-cellContent": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  width: "100%",
                  height: "100%",
                  overflow: "visible",
                },
              },
              "& .ai-hub-actions-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
